import { defineStore } from "pinia";
import { ref, watch, computed } from "vue";
import { useToast } from "vue-toastification";
import { event } from "vue-gtag";
import i18n from "@/i18n";
import { Organisation } from "@/types/company";
import { Scopes } from "@/types/user";

export const useAppStore = defineStore("app", () => {
  const toast = useToast();

  const route = useRoute();
  const router = useRouter();

  const currentRoute = ref(null);

  const currentOrganisationId = computed(() => {
    return route.params.companyId as string
  });
  const currentCampaignId = computed(() => {
    return route.params.campaignId as string
  });
  const currentSpaceId = computed(() => {
    return route.params.spaceId as string
  });

  // Sidebar
  const sidebarIsMounted = ref(false);
  const sidebarIsOpen = ref(true);
  const sidebarFooterIsOpen = ref(false);

  // Campaign
  const currentCampaignTab = ref('Overview');
  const activatedTableHasNews = ref({});
  const ponderationIsVisible = ref(false);
  const showCampaignLinkInfo = ref(false);
  const showCampaignParameters = ref(false);
  const campaignParametersTab = ref(0);
  
  // Talent panel
  const talentResultPanelIsOpen = ref(false);
  const openedCandidateResults = ref([]);
  const talentPanelDisplayMode = ref('LIST'); // can be LIST or GRAPH cf. enum DisplayMode
  const talentPanelPonderationIsOpen = ref(false);

  // Payment
  const creditsNeeded = ref(1);

  // News
  const viewedNewsIds = ref([]);
  const skipNewsForSession = ref(false);

  // Load viewed news IDs from localStorage
  if (localStorage.getItem('viewedNews')) {
    try {
      viewedNewsIds.value = JSON.parse(localStorage.getItem('viewedNews'));
    } catch (e) {
      viewedNewsIds.value = [];
    }
  }

  const currentNews = computed(() => {
    const { tm } = i18n.global;

    const newsRaw = tm("news", { returnObjects: true });
    const news = Object.entries(newsRaw).map(([id, item]) => ({ id, ...item }));

    if (!news || news.length === 0 || skipNewsForSession.value) return null;

    // Find the first news that hasn't been viewed and is within date range
    const now = new Date();
    for (let i = news.length - 1; i >= 0; i--) {
      const newsItem = news[i];

      // Check if news has date limits
      if (newsItem.validUntil && new Date(newsItem.validUntil) < now) {
        continue; // Skip this news if it's expired
      }

      if (newsItem.validFrom && new Date(newsItem.validFrom) > now) {
        continue; // Skip this news if it's not yet valid
      }

      // Return this news if it hasn't been viewed
      if (!viewedNewsIds.value.includes(newsItem.id)) {
        return newsItem;
      }
    }

    return null; // No valid news to show
  });

  function openTalentPanel() {
    talentResultPanelIsOpen.value = false;
  }

  // News funcs
  function dismissNews(dontShowAgain) {
    if (currentNews.value) {
      if (dontShowAgain) {
        // Add current news ID to viewed list
        viewedNewsIds.value.push(currentNews.value.id);

        // Save to localStorage
        localStorage.setItem('viewedNews', JSON.stringify(viewedNewsIds.value));
      }

      // No matter what, prevent showing more news until next refresh
      skipNewsForSession.value = true;
    }
  }

  // Sidebar funcs
  function toggleSidebar(value) {
    sidebarIsOpen.value = value;

    toggleSidebarFooter(false);
  }

  function toggleSidebarFooter(value) {
    sidebarFooterIsOpen.value = value;
  }

  // Campaign funcs
  function changeTab(tab: 'Overview' | 'Tracking' | 'Results') {
    currentCampaignTab.value = tab;
    router.replace({
      query: { ...route.query, tab: tab },
    });
  }

  async function exportPdf(pdfType, participantId) {
    try {
      const { data } = await useAxios.get(`/api/v1/participations/${participantId}/pdf/${pdfType}`, {}, true);

      return data;
    } catch (e) {
      console.error(e);
      throw e;
    }
  }

  async function exportCsv(campaignId) {
    try {
      const { data } = await useAxios.get(`/api/v1/campaigns/${campaignId}/csv`, {}, true);

      return data;
    } catch (e) {
      console.error(e);
      throw e;
    }
  }

  return {
    currentCampaignId,
    currentOrganisationId,
    currentSpaceId,
    activatedTableHasNews,
    openedCandidateResults,
    talentResultPanelIsOpen,
    talentPanelDisplayMode,
    talentPanelPonderationIsOpen,
    creditsNeeded,
    currentCampaignTab,
    currentRoute,
    ponderationIsVisible,
    showCampaignLinkInfo,
    showCampaignParameters,
    campaignParametersTab,
    sidebarFooterIsOpen,
    sidebarIsMounted,
    sidebarIsOpen,
    currentNews,
    openTalentPanel,
    toggleSidebar,
    toggleSidebarFooter,
    changeTab,
    dismissNews,
    exportPdf,
    exportCsv,
  };
});
